/**
 * useHasOmsSubscription (OCP-212) — shared hook that answers "does
 * the current customer hold an EXPLICIT OMS subscription?". Used by
 * MailLayout to filter the tab list, and by MailFeatureGuard to
 * redirect SaaS-tenant-only customers away from gated routes.
 *
 * Caches the answer at module scope after the first successful fetch
 * so multiple consumers in the same render tree share one round-trip.
 * AuthContext clears the cache on logout (via the resetOmsCache
 * helper) so a different customer logging in on the same browser
 * doesn't inherit the previous one's answer.
 *
 * "Has OMS sub" = at least one portal_subscriptions row with
 * product_family='oms' AND status IN (active, trialing, past_due).
 * Same set the API guard checks (PortalSubscriptionsService.
 * hasActiveOmsSubscription). The SaaS-tenant-included mail is NOT a
 * portal_subscription row, so SaaS-tenant-only customers return false
 * here — exactly the gate we want.
 */
import { useEffect, useState } from 'react';

import { ApiError, api } from './api';

type CacheState =
  | { state: 'unknown' }
  | { state: 'loading'; promise: Promise<boolean> }
  | { state: 'resolved'; hasOms: boolean }
  | { state: 'error'; message: string };

let cache: CacheState = { state: 'unknown' };
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

interface Subscription {
  id: string;
  product_family: string;
  status: string;
}

const LIVE_STATUSES = new Set(['active', 'trialing', 'past_due']);

async function fetchOmsState(): Promise<boolean> {
  const r = await api<{ items: Subscription[] }>('/v1/portal/me/subscriptions');
  return r.items.some(
    (s) => s.product_family === 'oms' && LIVE_STATUSES.has(s.status),
  );
}

function loadCache(): Promise<boolean> {
  if (cache.state === 'resolved') return Promise.resolve(cache.hasOms);
  if (cache.state === 'loading') return cache.promise;
  const promise = fetchOmsState()
    .then((hasOms) => {
      cache = { state: 'resolved', hasOms };
      notify();
      return hasOms;
    })
    .catch((err: unknown) => {
      const message =
        err instanceof ApiError ? `HTTP ${err.status}` : 'fetch failed';
      cache = { state: 'error', message };
      notify();
      throw err;
    });
  cache = { state: 'loading', promise };
  notify();
  return promise;
}

export interface OmsSubscriptionState {
  loading: boolean;
  hasOms: boolean | null;
  error: string | null;
  /** Force a re-fetch on the next consumer mount. */
  reload: () => void;
}

export function useHasOmsSubscription(): OmsSubscriptionState {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (cache.state === 'unknown') void loadCache();
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  // Touch `tick` so React tracks the re-render dependency on cache
  // changes. The value itself is unused.
  void tick;

  if (cache.state === 'resolved') {
    return { loading: false, hasOms: cache.hasOms, error: null, reload };
  }
  if (cache.state === 'error') {
    return { loading: false, hasOms: null, error: cache.message, reload };
  }
  return { loading: true, hasOms: null, error: null, reload };
}

function reload(): void {
  cache = { state: 'unknown' };
  notify();
}

/**
 * Called from AuthContext on logout so a different customer landing
 * on the same browser session sees a fresh fetch instead of the
 * previous customer's cached answer.
 */
export function resetOmsSubscriptionCache(): void {
  cache = { state: 'unknown' };
  notify();
}
